import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, X } from 'lucide-react';

/** La ficha de Play. Paquete `com.manusplit.app`. */
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.manusplit.app';

const DISMISSED_KEY = 'manusplit_android_link_dismissed';

/**
 * El enlace a la aplicación de Android, que se cierra y no vuelve.
 *
 * ## Se descarta para siempre, y eso es deliberado
 *
 * El aviso de instalar la PWA que ya había (`InstallPrompt`) se descarta por
 * **treinta días** y vuelve. Aquí no: quien cierra esto está diciendo «ya sé que
 * existe la app y no la quiero», y esa respuesta no caduca. Un aviso que
 * reaparece cada mes para decir lo mismo deja de leerse y empieza a molestar,
 * que es justo lo contrario de reducir fricción.
 *
 * Por eso la marca es un `'1'` y no una fecha: no hay nada que comparar.
 *
 * ## Quién decide dónde sale
 *
 * Aquí no se mira la plataforma. En escritorio sale siempre —tener la web
 * abierta en el ordenador es el momento más natural para llevarse la app al
 * teléfono— y en móvil sólo si es Android, porque en un iPhone sería un enlace
 * a algo que no se puede instalar. Esa distinción la hace quien lo monta, que es
 * quien sabe en qué hueco está; el componente sólo sabe pintarse y callarse.
 */
export function AndroidAppLink({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      // Sin almacenamiento se enseña: es preferible a esconder algo útil por
      // no poder recordar una preferencia.
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* ver arriba */
    }
    setDismissed(true);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand hover:bg-background flex min-w-0 flex-1 items-center gap-2.5 rounded px-3 py-2 text-sm transition-colors"
      >
        <Smartphone size={18} className="shrink-0" />
        <span className="truncate">{t('install.android_app_link')}</span>
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('common.close')}
        className="text-muted hover:text-ink shrink-0 rounded p-1 transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
}
